import { useState, useRef, useCallback } from 'react'

export interface HoverState {
  faceId: string
  cropUrl: string
  triggerRect: DOMRect
}

export function useFaceHover(enterDelay = 200, leaveDelay = 150) {
  const [hoverState, setHoverState] = useState<HoverState | null>(null)
  const enterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleMouseEnter = useCallback(
    (faceId: string, cropUrl: string, element: HTMLElement) => {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current)
        leaveTimeoutRef.current = null
      }
      enterTimeoutRef.current = setTimeout(() => {
        const rect = element.getBoundingClientRect()
        setHoverState({ faceId, cropUrl, triggerRect: rect })
      }, enterDelay)
    },
    [enterDelay]
  )

  const handleMouseLeave = useCallback(() => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current)
      enterTimeoutRef.current = null
    }
    leaveTimeoutRef.current = setTimeout(() => {
      setHoverState(null)
    }, leaveDelay)
  }, [leaveDelay])

  const handlePreviewEnter = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
      leaveTimeoutRef.current = null
    }
  }, [])

  const handlePreviewLeave = useCallback(() => {
    leaveTimeoutRef.current = setTimeout(() => {
      setHoverState(null)
    }, leaveDelay)
  }, [leaveDelay])

  return {
    hoverState,
    handleMouseEnter,
    handleMouseLeave,
    handlePreviewEnter,
    handlePreviewLeave,
  }
}
